import React, { useState, useEffect } from "react";
import axios from "axios";

function Assignment() {
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [assignments, setAssignments] = useState([]);

  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");

  const [assignmentTitle, setAssignmentTitle] = useState("");
  const [marks, setMarks] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [currentAssignmentId, setCurrentAssignmentId] = useState(null);

  // SESSION ID FROM LOCAL STORAGE 
  const sessionId = localStorage.getItem("currentSessionId");

  // FIXED STYLING
  const labelStyle = "block font-semibold text-black text-[18px] mb-2";
  const inputStyle =
    "mt-1 p-2 block w-full border border-gray-300 rounded-md text-[14px] focus:ring-indigo-500 focus:border-indigo-500";

  // -------------------------------------------------------------------
  // 1️⃣ FETCH CLASSES BY SESSION
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!sessionId) return;

    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/classes/getClasses?sessionId=${sessionId}`)
      .then((response) => {
        setClasses(response.data);
      })
      .catch(() => alert("Error fetching classes"));
  }, [sessionId]);

  // -------------------------------------------------------------------
  // 2️⃣ FETCH SECTIONS + SUBJECTS when CLASS CHANGES
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!selectedClass || !sessionId) return;

    const encodedClass = encodeURIComponent(selectedClass);

    Promise.all([
      // SECTIONS
      // Sections
      axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/sections/${encodeURIComponent(selectedClass)}/getSectionsByClassName`,
        { params: { sessionId } }
      ),


      // SUBJECTS
      axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/subjects/getSubjectsByClassName/${encodedClass}`,
        { params: { sessionId } }
      ),
    ])
      .then(([sectionRes, subjectRes]) => {
        setSections(sectionRes.data);
        setSubjects(subjectRes.data);
      })
      .catch(() => alert("Error fetching sections or subjects"));
  }, [selectedClass, sessionId]);

  // -------------------------------------------------------------------
  // 3️⃣ FETCH ALL ASSIGNMENTS
  // -------------------------------------------------------------------
  const fetchAssignments = () => {
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/assignments/getAssignments`, {
        params: { sessionId }
      })
      .then((res) => setAssignments(res.data))
      .catch(() => alert("Error fetching assignments"));
  };

  useEffect(() => {
    fetchAssignments();
  }, []);

  // -------------------------------------------------------------------
  // FILE
  // -------------------------------------------------------------------
  const handleFileChange = (e) => setFile(e.target.files[0]);

  // -------------------------------------------------------------------
  // 4️⃣ SAVE / UPDATE ASSIGNMENT
  // -------------------------------------------------------------------
  const handleSubmit = (e) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    e.preventDefault();
    const formData = new FormData();

    formData.append("assignmentTitle", assignmentTitle);
    formData.append("description", description);
    formData.append("className", selectedClass);
    formData.append("sectionName", selectedSection);
    formData.append("subjectName", selectedSubject);
    formData.append("marks", marks);
    formData.append("dueDate", dueDate);
    formData.append("sessionId", sessionId);

    if (file) formData.append("file", file);

    if (isEditing) {
      axios
        .put(
          `${process.env.REACT_APP_API_BASE_URL}/api/assignments/updateAssignment/${currentAssignmentId}`,
          formData
        )
        .then(() => {
          alert("Assignment updated!");
          resetForm();
          fetchAssignments();
        })
        .catch(() => alert("Error updating assignment"));
    } else {
      axios
        .post(`${process.env.REACT_APP_API_BASE_URL}/api/assignments/createAssignment`, formData)
        .then(() => {
          alert("Assignment created!");
          resetForm();
          fetchAssignments();
        })
        .catch(() => alert("Error creating assignment"));
    }
  };

  // -------------------------------------------------------------------
  // RESET FORM
  // -------------------------------------------------------------------
  const resetForm = () => {
    setAssignmentTitle("");
    setDescription("");
    setSelectedClass("");
    setSelectedSection("");
    setSelectedSubject("");
    setMarks("");
    setDueDate("");
    setFile(null);
    setIsEditing(false);
    setCurrentAssignmentId(null);
  };

  // -------------------------------------------------------------------
  // EDIT
  // -------------------------------------------------------------------
  const handleEditClick = (assignment) => {
    setAssignmentTitle(assignment.assignmentTitle);
    setDescription(assignment.description);
    setSelectedClass(assignment.className);
    setSelectedSection(assignment.sectionName);
    setSelectedSubject(assignment.subjectName);
    setMarks(assignment.marks);
    setDueDate(assignment.dueDate.split("T")[0]);
    setIsEditing(true);
    setCurrentAssignmentId(assignment._id);
  };

  // -------------------------------------------------------------------
  // DELETE
  // -------------------------------------------------------------------
  const handleDeleteClick = (id) => {
    if (!window.confirm("Delete assignment?")) return;

    axios
      .delete(`${process.env.REACT_APP_API_BASE_URL}/api/assignments/deleteAssignment/${id}`)
      .then(() => {
        fetchAssignments();
      })
      .catch(() => alert("Error deleting assignment"));
  };

  // -------------------------------------------------------------------
  // JSX
  // -------------------------------------------------------------------
  return (
    <div className="max-w-full mx-auto p-4 bg-white mt-4">
      <form onSubmit={handleSubmit}>
        {/* CLASS - SECTION - SUBJECT */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

          {/* CLASS */}
          <div>
            <label className={labelStyle}>Class</label>
            <select
              className={inputStyle}
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
            >
              <option value="">Select Class</option>
              {classes.map((c) => (
                <option key={c._id} value={c.className}>
                  {c.className}
                </option>
              ))}
            </select>
          </div>

          {/* SECTION */}
          <div>
            <label className={labelStyle}>Section</label>
            <select
              className={inputStyle}
              value={selectedSection}
              onChange={(e) => setSelectedSection(e.target.value)}
            >
              <option value="">Select Section</option>
              {sections.map((sec) => (
                <option key={sec._id} value={sec.sectionName}>
                  {sec.sectionName}
                </option>
              ))}
            </select>
          </div>

          {/* SUBJECT */}
          <div>
            <label className={labelStyle}>Subject</label>
            <select
              className={inputStyle}
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
            >
              <option value="">Select Subject</option>
              {subjects.length > 0 ? (
                subjects.map((sub) => (
                  <option key={sub._id} value={sub.subjectName}>
                    {sub.subjectName}
                  </option>
                ))
              ) : (
                <option disabled>Loading subjects...</option>
              )}
            </select>
          </div>
        </div>

        {/* TITLE - DUE DATE */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
          <div>
            <label className={labelStyle}>Assignment Title</label>
            <input
              type="text"
              className={inputStyle}
              value={assignmentTitle}
              onChange={(e) => setAssignmentTitle(e.target.value)}
              required
            />
          </div>

          <div>
            <label className={labelStyle}>Due Date</label>
            <input
              type="date"
              className={inputStyle}
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              required
            />
          </div>
        </div>

        {/* MARKS + FILE */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
          <div>
            <label className={labelStyle}>Marks</label>
            <input
              type="number"
              className={inputStyle}
              value={marks}
              onChange={(e) => setMarks(e.target.value)}
            />
          </div>

          <div>
            <label className={labelStyle}>Upload File</label>
            <input type="file" className={inputStyle} onChange={handleFileChange} />
          </div>
        </div>

        {/* DESCRIPTION */}
        <div className="mt-4">
          <label className={labelStyle}>Description</label>
          <textarea
            className={inputStyle}
            rows="4"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
          ></textarea>
        </div>

        {/* BUTTONS */}
        <div className="mt-6 flex gap-3">
          <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded">
            {isEditing ? "Update Assignment" : "Create Assignment"}
          </button>

          {isEditing && (
            <button
              onClick={resetForm}
              className="bg-gray-500 text-white px-4 py-2 rounded"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* ASSIGNMENTS LIST */}
      <div className="mt-10">
        <h2 className="text-[18px] font-semibold text-black mb-4">Created Assignments</h2>

        <table className="min-w-full bg-white text-center border">
          <thead>
            <tr className="bg-gray-200">
              <th className="p-2 border">Sr No.</th>
              <th className="p-2 border">Title</th>
              <th className="p-2 border">Class</th>
              <th className="p-2 border">Subject</th>
              <th className="p-2 border">Marks</th>
              <th className="p-2 border">Due Date</th>
              <th className="p-2 border">Action</th>
            </tr>
          </thead>

          <tbody>
            {assignments.map((assignment, index) => (
              <tr key={assignment._id}>
                <td className="border p-2">{index + 1}</td>
                <td className="border p-2">{assignment.assignmentTitle}</td>
                <td className="border p-2">
                  {assignment.className} ({assignment.sectionName})
                </td>
                <td className="border p-2">{assignment.subjectName}</td>
                <td className="border p-2">{assignment.marks}</td>
                <td className="border p-2">
                  {new Date(assignment.dueDate).toLocaleDateString()}
                </td>

                <td className="border p-2">
                  <div className="flex gap-2 justify-center">
                    <button
                      className="bg-yellow-500 text-white px-3 py-1 rounded"
                      onClick={() => handleEditClick(assignment)}
                    >
                      Edit
                    </button>

                    <button
                      className="bg-red-600 text-white px-3 py-1 rounded"
                      onClick={() => handleDeleteClick(assignment._id)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {assignments.length === 0 && (
              <tr>
                <td colSpan="7" className="p-4 text-gray-500">No assignments found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Assignment;
